import React, { useState, useEffect, useMemo, Dispatch } from 'react'
import { css } from '@emotion/core'
import { DatePicker } from '@/components/organisms/reservation/_detail-edit-info/date-picker'
import { useTranslation } from 'react-i18next'
import { fetchReservationChannelCode, fetchReservationMarketSegment } from '@/apis/aipass'
import dayjs from 'dayjs'
import { TimePicker } from '@/components/atoms/time-picker'
import moment from 'moment'
import { ReservationType } from '@/models/reservation'

export type ReservationFormType = {
  plan: any
  checkinDate: any
  checkoutDate: any
  checkinTime: any
  checkoutTime: any
  accommodationFee: any
  pointDiscount: any
  payment: any
  notes: any
  channelCodeId: any
  marketSegmentId: any
  meal: any
}

type Props = {
  reservation: ReservationType
  reservationForm: ReservationFormType
  setReservationForm: Dispatch<ReservationFormType>
  setErrors: Dispatch<string[]>
}

export const ReservationForm: React.FC<Props> = ({ reservationForm, setReservationForm, setErrors, ...props }) => {
  const { t } = useTranslation()
  const [initializedComponent, setInitializedComponent] = useState<boolean>(false)
  const [checkedBreakfast, setCheckedBreakfast] = useState<boolean>(false)
  const [checkedLunch, setCheckedLunch] = useState<boolean>(false)
  const [checkedDinner, setCheckedDinner] = useState<boolean>(false)
  const [checkedIncluded1Meal, setCheckedIncluded1Meal] = useState<boolean>(false)
  const [checkedIncluded2Meal, setCheckedIncluded2Meal] = useState<boolean>(false)
  const [reservationChannelCode, setReservationChannelCode] = useState<any>([])
  const [marketSegment, setMarketSegment] = useState<any>([])
  const defaultPaymentList = ['現地決済', 'カード決済', 'エージェント精算', 'その他']
  const isCustomPaymentMethod = useMemo(() => {
    return reservationForm.payment && !defaultPaymentList.includes(reservationForm.payment)
  }, [reservationForm])

  const parseMealText = (meal: string) => {
    if (meal.includes('朝')) {
      setCheckedBreakfast(true)
    }
    if (meal.includes('昼')) {
      setCheckedLunch(true)
    }
    if (meal.includes('夕')) {
      setCheckedDinner(true)
    }
    if (meal.includes('1食')) {
      setCheckedIncluded1Meal(true)
    }
    if (meal.includes('2食')) {
      setCheckedIncluded2Meal(true)
    }
  }

  const onChangeFormValue = (key: keyof ReservationFormType, value: any) => {
    setReservationForm({ ...reservationForm, [key]: value })
  }

  const onChangeChannelCode = (selectedId: string) => {
    const editingFormValue = {
      channelCodeId: reservationForm.channelCodeId,
      marketSegmentId: reservationForm.marketSegmentId,
    }
    const selectedChannel = reservationChannelCode.find(master => master.id === selectedId)
    if (!selectedChannel) {
      setReservationForm({ ...reservationForm, channelCodeId: undefined })
      return
    }
    editingFormValue.channelCodeId = selectedChannel.id
    if (selectedChannel.suggestSegmentId && !reservationForm.marketSegmentId) {
      const isEnabledSegment = marketSegment.findIndex(master => master.id === selectedChannel.suggestSegmentId) !== -1
      if (isEnabledSegment) {
        editingFormValue.marketSegmentId = selectedChannel.suggestSegmentId
      }
    }
    setReservationForm({ ...reservationForm, ...editingFormValue })
  }

  const validateNumericFields = ({ accommodationFee, pointDiscount }: ReservationFormType) => {
    const isHalfSizeNumberAccommodationFee = accommodationFee && Boolean(!String(accommodationFee).match(/^[0-9]+$/))
    const isHalfSizeNumberPointDiscount = pointDiscount && Boolean(!String(pointDiscount).match(/^[0-9]+$/))
    if (isHalfSizeNumberAccommodationFee || isHalfSizeNumberPointDiscount) {
      return 'Please enter accommodation fee and point discount in single-byte numbers'
    }
  }

  const validateCheckinDate = ({ checkinDate, checkoutDate, checkinTime, checkoutTime }: ReservationFormType) => {
    // Accommodation date before change
    const checkinDateWithoutHour = checkinDate?.includes(' ') ? checkinDate.split(' ')[0] : checkinDate // Default BE return YYYY-MM-DD HH:MM:SS
    const checkoutDateWithoutHour = checkoutDate?.includes(' ') ? checkoutDate.split(' ')[0] : checkoutDate // Default BE return YYYY-MM-DD HH:MM:SS

    if (dayjs(checkoutDate).format('YYYY-MM-DD') < dayjs(checkinDate).format('YYYY-MM-DD')) {
      return 'Please set the check-out date after the check-in date'
    }

    if (
      checkinTime &&
      checkoutTime &&
      dayjs(`${checkoutDateWithoutHour} ${checkoutTime}`).isSameOrBefore(`${checkinDateWithoutHour} ${checkinTime}`)
    ) {
      return 'Checkout time before checkin time'
    }
  }

  useEffect(() => {
    setErrors([validateCheckinDate(reservationForm), validateNumericFields(reservationForm)].filter((msg): msg is string => !!msg))
  }, [reservationForm])

  useEffect(() => {
    if (!initializedComponent) {
      return
    }
    const saveMealList: string[] = []
    if (checkedBreakfast) {
      saveMealList.push('朝食')
    }
    if (checkedLunch) {
      saveMealList.push('昼食')
    }
    if (checkedDinner) {
      saveMealList.push('夕食')
    }
    if (checkedIncluded1Meal) {
      saveMealList.push('1食付き')
    }
    if (checkedIncluded2Meal) {
      saveMealList.push('2食付き')
    }
    onChangeFormValue('meal', saveMealList.join('/') || '-')
  }, [checkedBreakfast, checkedLunch, checkedDinner, checkedIncluded1Meal, checkedIncluded2Meal])

  useEffect(() => {
    parseMealText(reservationForm?.meal || '')
    fetchReservationChannelCode().then(setReservationChannelCode)
    fetchReservationMarketSegment().then(setMarketSegment)
    setInitializedComponent(true)
  }, [])

  return (
    <>
      <div css={topContentStyle}>
        <div css={checkinDateCalenderStyle}>
          <div>
            <div css={checkinDateStyle}>{t('Date of stay')}</div>
            <DatePicker date={reservationForm.checkinDate} onDateChange={v => onChangeFormValue('checkinDate', v)} />
            <span css={wavyLineStyle}>~</span>
            <DatePicker date={reservationForm.checkoutDate} onDateChange={v => onChangeFormValue('checkoutDate', v)} />
          </div>
          <div css={arrivalWrapper}>
            <div css={checkinDateStyle}>{t('Checkin time')}</div>
            <TimePicker
              minuteStep={15}
              value={reservationForm.checkinTime ? (moment(reservationForm.checkinTime, 'HH:mm') as any) : undefined}
              onChange={value => {
                onChangeFormValue('checkinTime', value?.format('HH:mm'))
              }}
            />
          </div>
          <div css={arrivalWrapper}>
            <div css={checkinDateStyle}>{t('Checkout time')}</div>
            <TimePicker
              minuteStep={15}
              value={reservationForm.checkoutTime ? (moment(reservationForm.checkoutTime, 'HH:mm') as any) : undefined}
              onChange={value => {
                onChangeFormValue('checkoutTime', value?.format('HH:mm'))
              }}
            />
          </div>
        </div>
      </div>

      <div css={bottomContainerStyle}>
        <div css={topFormWrapperStyle}>
          <div css={inputWrapperStyle}>
            <p css={subTitleStyle}>{t('Channel')}</p>
            <div>
              {reservationForm && (
                <select
                  name="channelCode"
                  css={selectStyle}
                  onChange={e => {
                    onChangeChannelCode(e.target.value)
                  }}
                  value={reservationForm.channelCodeId || ''}
                >
                  <option value="">-</option>
                  {reservationChannelCode.map(itemChannelCode => (
                    <option key={itemChannelCode.id} value={itemChannelCode.id}>
                      {itemChannelCode.name}
                    </option>
                  ))}
                  {props.reservation.channelCodeId && !reservationChannelCode.some(({ id }) => reservationForm.channelCodeId === id) && (
                    <option value={props.reservation.channelCodeId} selected>
                      {props.reservation.channelCodeName}
                    </option>
                  )}
                </select>
              )}
            </div>
          </div>

          <div>
            <p css={subTitleStyle}>{t('Market segment')}</p>
            <select
              name="marketSegmentId"
              css={selectStyle}
              onChange={e => onChangeFormValue('marketSegmentId', e.target.value)}
              value={reservationForm.marketSegmentId || ''}
            >
              <option value="">-</option>
              {marketSegment.map(itemSegment => (
                <option key={itemSegment.id} value={itemSegment.id}>
                  {itemSegment.name}
                </option>
              ))}
              {props.reservation.marketSegmentId && !marketSegment.some(({ id }) => props.reservation.marketSegmentId === id) && (
                <option value={props.reservation.marketSegmentId} selected>
                  {props.reservation.marketSegmentName}
                </option>
              )}
            </select>
          </div>
        </div>
        <p css={subTitleStyle}>{t('Plan name')}</p>
        <div>
          <input
            type="value"
            name="plan"
            value={reservationForm.plan}
            onChange={e => onChangeFormValue('plan', e.target.value)}
            css={planStyle}
          ></input>
        </div>

        <div css={sharedContainerStyle}>
          <div>
            <p css={subTitleStyle}>{t('Meal')}</p>
            <div css={mealPlanInnerStyle}>
              <label>
                <input
                  alt="checkbox"
                  type="image"
                  onClick={() => setCheckedBreakfast(!checkedBreakfast)}
                  src={require(`@/static/images/check-box${checkedBreakfast ? '' : '_off'}.svg`)}
                />
                <p>{t('Breakfast')}</p>
              </label>
              <label>
                <input
                  alt="checkbox"
                  type="image"
                  onClick={() => setCheckedLunch(!checkedLunch)}
                  src={require(`@/static/images/check-box${checkedLunch ? '' : '_off'}.svg`)}
                />
                <p>{t('Lunch')}</p>
              </label>
              <label>
                <input
                  alt="checkbox"
                  type="image"
                  onClick={() => setCheckedDinner(!checkedDinner)}
                  src={require(`@/static/images/check-box${checkedDinner ? '' : '_off'}.svg`)}
                />
                <p>{t('Dinner')}</p>
              </label>
              <label>
                <input
                  alt="checkbox"
                  type="image"
                  onClick={() => setCheckedIncluded1Meal(!checkedIncluded1Meal)}
                  src={require(`@/static/images/check-box${checkedIncluded1Meal ? '' : '_off'}.svg`)}
                />
                <p>{t('1 meal included')}</p>
              </label>
              <label>
                <input
                  alt="checkbox"
                  type="image"
                  onClick={() => setCheckedIncluded2Meal(!checkedIncluded2Meal)}
                  src={require(`@/static/images/check-box${checkedIncluded2Meal ? '' : '_off'}.svg`)}
                />
                <p>{t('2 meal included')}</p>
              </label>
            </div>
          </div>
        </div>
        <div css={paymentPlanStyle}>
          <div>
            <ul>
              <li css={subTitleStyle}>{t('Accommodation fee')}</li>
              <li>
                <input
                  type="value"
                  name="cost"
                  value={reservationForm.accommodationFee}
                  onChange={e => onChangeFormValue('accommodationFee', e.target.value)}
                  css={paymentCostStyle}
                ></input>
              </li>
            </ul>
          </div>
          <div>
            <ul>
              <li css={subTitleStyle}>{t('Point discount')}</li>
              <li>
                <input
                  type="value"
                  name="point"
                  value={reservationForm.pointDiscount}
                  onChange={e => onChangeFormValue('pointDiscount', e.target.value)}
                  css={paymentCostStyle}
                ></input>
              </li>
            </ul>
          </div>
          <div>
            <ul>
              <li css={subTitleStyle}>{t('Method of payment')}</li>
              <li>
                <select
                  name="type"
                  css={paymentMethodStyle}
                  value={reservationForm.payment}
                  onChange={e => onChangeFormValue('payment', e.target.value)}
                >
                  {defaultPaymentList.map((type, index) => (
                    <option key={index} value={type}>
                      {type}
                    </option>
                  ))}
                  {isCustomPaymentMethod && <option value={reservationForm.payment}>{reservationForm.payment}</option>}
                </select>
              </li>
            </ul>
          </div>
        </div>
        <div css={memoStyle}>
          <div>
            <p css={subTitleStyle}>{t('Remarks')}</p>
            <div>
              <textarea
                name="memo"
                value={reservationForm.notes}
                onChange={e => onChangeFormValue('notes', e.target.value)}
                css={memoTextStyle}
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const selectStyle = css({
  width: 376,
  display: 'block',
  height: 32,
  borderRadius: 16,
  marginBottom: 24,
  textAlign: 'left',
  border: '1px solid #CCCCCC',
  appearance: 'none',
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  paddingLeft: 20,
  paddingRight: 40,
  cursor: 'pointer',
  backgroundPosition: '92% center',
})

const arrivalWrapper = css({
  marginLeft: 16,
})

const topContentStyle = css({
  padding: '32px 0 6px 37px',
  borderBottom: '1px solid #E3E3E3',
  backgroundColor: '#FFFFFF',
  borderRadius: '5px 5px 0 0',
})

const bottomContainerStyle = css({
  padding: '24px 32px 30px 32px',
  backgroundColor: '#FFFFFF',
  borderRadius: '0 0 5px 5px',
})

const methodStyle = css({
  margin: '0 0 24px 0',
  padding: '9px 0 9px 16px',
  width: '90%',
  height: 32,
  fontSize: 14,
  border: '1px solid #CCCCCC',
  borderRadius: 17,
  backgroundColor: '#FFFFFF',
})

const planStyle = css(methodStyle, {
  minWidth: 631,
  width: '100%',
})

const checkinDateCalenderStyle = css({
  margin: '0 0 23px 0',
  display: 'flex',
  alignItems: 'center',
  fontSize: 14,
  '.DateInput': {
    width: 115,
  },
})

const wavyLineStyle = css({
  padding: '0 7px',
})

const textStyle = css({
  margin: '0 40px 0 8px',
  padding: '10px 0',
  fontSize: 14,
  fontWeight: 'bold',
  color: '#676767',
})

const subTitleStyle = css(textStyle, {
  margin: '0 16px 12px 0',
  padding: 0,
  fontSize: 12,
})

const nightsStyle = css({
  width: 78,
  height: 32,
  padding: '8px 16px',
  border: '1px solid #CCCCCC',
  borderRadius: 17,
  textAlign: 'left',
  appearance: 'none',
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 52,
  backgroundSize: 10,
})

const sharedContainerStyle = css({
  margin: '0 0 24px 0',
  display: 'flex',
  alignItems: 'start',
  fontSize: 14,
})

const mealPlanInnerStyle = css({
  fontSize: 14,
  img: {
    width: 20,
    height: 20,
  },
  p: {
    display: 'inline-table',
    margin: '0 24px 0 10px',
    padding: '4px 0',
  },
})

const paymentPlanStyle = css({
  display: 'flex',
  margin: '0 0 24px 0',
  alignItems: 'start',
  fontSize: 14,
})

const paymentCostStyle = css(nightsStyle, {
  width: 187,
  margin: '0 16px 0 0',
  background: 'none',
  fontSize: 14,
})

const paymentMethodStyle = css(nightsStyle, {
  width: 187,
  backgroundPosition: 150,
  fontSize: 14,
})

const memoStyle = css({
  display: 'flex',
  alignItems: 'start',
  fontSize: 14,
})

const memoTextStyle = css(nightsStyle, {
  width: 636,
  height: 'auto',
  background: 'none',
  border: '1px solid #CCCCCC',
  borderRadius: 12,
  resize: 'none',
})

const checkinDateStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 12,
  color: '#676767',
  fontWeight: 'bold',
  marginBottom: 12,
})

const inputWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  marginRight: 0,
})

const topFormWrapperStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})
